import { Button, Divider } from 'antd';
import { EyeOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

/**
 * Creates conditional rating columns based on reviewActiveKey.
 * This utility function eliminates code duplication by generating the common structure
 * used across multiple rating list components.
 * @function
 *
 * @param {Object} params - Configuration parameters
 * @param {Function} params.t - Translation function from useTranslation hook
 * @param {string} params.reviewActiveKey - The active review key ('received_reviews' or 'left_reviews')
 * @returns {Array} Array of conditional column configuration objects
 */
export const createConditionalRatingColumns = ({ t, reviewActiveKey }) => {
  if (reviewActiveKey === 'received_reviews') {
    return [
      {
        title: t('ratings.form.rating_reference'),
        key: 'author.id.reference',
        dataIndex: 'author',
        render: (author) => author?.id?.reference || '-'
      },
      {
        title: t('ratings.form.left_by'),
        key: 'author.id.name',
        dataIndex: 'author',
        render: (author) =>
          author?.id?.last_name || author?.id?.first_name
            ? `${author?.id?.last_name || ''} ${author?.id?.first_name || ''}`
            : '-'
      }
    ];
  }

  return [
    {
      title: t('ratings.form.rating_reference'),
      key: 'receiver.id.reference',
      dataIndex: 'receiver',
      render: (receiver) => {
        if (receiver?.id?.length) {
          return receiver?.id?.map((user) => user?.reference).join(', ');
        }
        return '-';
      }
    },
    {
      title: t('ratings.form.received_by'),
      key: 'receiver.id.name',
      dataIndex: 'receiver',
      render: (receiver) => {
        if (receiver?.id?.length) {
          return receiver.id
            .map(
              (user) =>
                `${
                  user?.civility ? t(`pensioners.tags.${user?.civility}`) : ''
                } ${user?.last_name || ''} ${user?.first_name || ''}`
            )
            .join(', ');
        }
        return '-';
      }
    }
  ];
};

/**
 * Creates standard rating columns configuration for data tables.
 * This utility function eliminates code duplication by generating the common structure
 * used across multiple rating list components.
 *
 * @param {Object} params - Configuration parameters
 * @param {Function} params.t - Translation function from useTranslation hook
 * @param {Function} params.setEditReviewDrawer - Function to set edit review drawer state
 * @param {Function} params.setShowReviewDrawer - Function to set show review drawer state
 * @param {Function} params.setReviewData - Function to set review data
 * @param {Function} params.setReviewDrawerOpen - Function to set review drawer open state
 * @returns {Array} Array of standard column configuration objects
 */
export const createStandardRatingColumns = ({
  t,
  setEditReviewDrawer,
  setShowReviewDrawer,
  setReviewData,
  setReviewDrawerOpen
}) => [
  {
    title: t('ratings.form.supervision_reference'),
    key: 'supervision',
    dataIndex: 'supervision',
    render: (supervision) => supervision?.reference || '-'
  },
  {
    title: t('ratings.form.supervision_city'),
    key: 'supervision',
    dataIndex: 'supervision',
    render: (supervision) =>
      supervision?.petsitting_address?.address?.city || '-'
  },
  {
    title: t('ratings.form.date'),
    key: 'date',
    dataIndex: 'date',
    render: (date) => dayjs(date).format('DD/MM/YYYY')
  },
  {
    title: t('ratings.form.rating'),
    key: 'rating',
    dataIndex: 'rating',
    render: (rating) => rating || '-'
  },
  {
    key: 'action',
    render: (text, record) => (
      <>
        <Button
          type="link"
          onClick={() => {
            setEditReviewDrawer(false);
            setShowReviewDrawer(true);
            setReviewData(record);
            setReviewDrawerOpen(true);
          }}
        >
          <EyeOutlined />
        </Button>
        <Divider type="vertical" />
        <Button
          type="link"
          onClick={() => {
            setShowReviewDrawer(false);
            setEditReviewDrawer(true);
            setReviewData(record);
            setReviewDrawerOpen(true);
          }}
        >
          <EditOutlined />
        </Button>
      </>
    )
  }
];
